import { TrashIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { toast } from 'sonner';
import { ORPCError } from '@orpc/client';
import { Action, Subject } from '@erp/authz';
import { useCan } from '@/hooks/useCan';
import { useDeleteMemoryBankNote } from '@/api/core/ai/memory-bank/mutations';
import { useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';

export function DeleteNoteButton({
  noteId,
  onDeleted,
}: {
  noteId: string;
  onDeleted?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [canDelete] = useCan(Action.Delete, Subject.MemoryBankNote);
  const mutation = useDeleteMemoryBankNote();
  const gt = useGT();
  const invalidate = useInvalidateRpcQueryFn(
    (orpc) => [
      orpc.memoryBank.list.key(),
      orpc.memoryBank.listUsages.key(),
      orpc.memoryBank.batchGet.key(),
      orpc.memoryBank.get.key(),
    ],
    [],
  );
  async function remove() {
    try {
      const result = await mutation.mutateAsync({ id: noteId });
      if (!result.success) {
        toast.error(gt('Could not delete note. Try again.'));
        return;
      }
      invalidate();
      onDeleted?.();
      setOpen(false);
      toast.success(gt('Note deleted.'));
    } catch (error) {
      // The server explains a refused legacy note; other failures stay generic.
      toast.error(
        error instanceof ORPCError && error.code === 'PRECONDITION_FAILED'
          ? error.message
          : gt('Could not delete note. Try again.'),
      );
    }
  }
  if (!canDelete) return null;
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 text-gray-400 hover:text-destructive hover:bg-gray-100"
        title={gt('Delete note')}
        aria-label={gt('Delete note')}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        <TrashIcon className="h-3 w-3" aria-hidden="true" />
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T>Delete note?</T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T>
                This note will stop being used for future matching. Its history
                will be retained. Previously reviewed records will not change.
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>
              <T>Cancel</T>
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={mutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                void remove();
              }}
            >
              {mutation.isPending ? <T>Deleting...</T> : <T>Delete note</T>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
